import {
  ColumnDef,
  ColumnFiltersState,
  RowSelectionState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { CSSProperties, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui";
import { cn } from "~/lib/utils";

import DataTablePaginate from "./paginate";
import TableRowSkeleton from "./table-skeleton";
import { DataTableToolbar } from "./toolbar";
import { TPaginationResponse, paginationParams } from "./types";

export type CustomColumnMeta = {
  omitRowClick?: boolean;
  pin?: "left" | "right";
};
// Extend the ColumnDef type to include the custom `meta` property
export type ExtendedColumnDef<TData, TValue> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};
interface DatatableProps<TData, TValue> {
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  pagination?: paginationParams;
  tableParams?: TPaginationResponse;
  toolbar?: { title: string; actions: React.ReactNode; backBtn?: boolean };
  filter?: {
    searchOnchange: (search?: string) => void;
    searchPlaceholder: string;
    searchColumn: string;
  };

  tablePrefixComponent?: React.FC;
  selectedRows?: string[];
  onRowClick?: (row: TData) => void;
  getRowHoverClass?: (row: TData) => string;
  // resetSelection?: boolean;
  rowSelection: RowSelectionState;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}
const Datatable = <TData, TValue>({
  data,
  columns,
  isLoading,
  pagination,
  tableParams,
  toolbar,
  rowSelection,
  setRowSelection,
  onRowClick,
  getRowHoverClass,
  ...props
}: DatatableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  const getCommonPinningStyles = (
    column: ExtendedColumnDef<TData, TValue>,
    isHeader: boolean = false,
  ): CSSProperties => {
    const pin = column.meta?.pin;
    const isPinned = !!pin;

    return {
      left: pin === "left" ? 0 : undefined,
      right: pin === "right" ? 0 : undefined,
      position: pin ? "sticky" : isHeader ? "sticky" : undefined,
      zIndex: pin ? (isHeader ? 40 : 30) : isHeader ? 20 : undefined,
      backgroundColor: isHeader
        ? isPinned
          ? "#CBFF10"
          : undefined
        : isPinned
          ? "white"
          : undefined,
      backgroundImage: !isHeader && isPinned ? "inherit" : undefined,
    };
  };

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex: tableParams?.pageIndex as number,
        pageSize: tableParams?.pageSize as number,
      },
    },
    manualPagination: true,
  });

  return (
    <div className="w-full space-y-4">
      <DataTableToolbar toolbar={toolbar} />
      {props.tablePrefixComponent && <props.tablePrefixComponent />}

      <div className="w-full overflow-x-auto rounded-t-2xl">
        <Table className="min-w-max bg-white">
          <TableHeader className="w-full">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className="z-10 bg-secondary-500 font-Bold text-black"
                      style={{
                        ...getCommonPinningStyles(
                          header.column.columnDef as ExtendedColumnDef<
                            TData,
                            TValue
                          >,
                          true,
                        ),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          {isLoading ? (
            <TableRowSkeleton
              columns={columns.length}
              rows={tableParams?.pageSize}
            />
          ) : (
            <TableBody className="rounded-t-2xl">
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    // data-state={row?.getIsSelected() && "selected"}
                    data-state={
                      rowSelection
                        ? row?.getIsSelected() && "selected"
                        : undefined
                    }
                    className={cn(
                      "text-primary-500",
                      getRowHoverClass?.(row.original),
                      {
                        "bg-secondary-200":
                          rowSelection && row?.getIsSelected(),
                      },
                    )}
                    onClick={() => onRowClick?.(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isNonClickable = (
                        cell.column.columnDef as ExtendedColumnDef<
                          TData,
                          TValue
                        >
                      ).meta?.omitRowClick;
                      return (
                        <TableCell
                          key={cell.id}
                          style={{
                            ...getCommonPinningStyles(
                              cell.column.columnDef as ExtendedColumnDef<
                                TData,
                                TValue
                              >,
                              false,
                            ),
                          }}
                          className={cn("", {
                            "hover:cursor-pointer":
                              onRowClick && !isNonClickable,
                            "bg-secondary-200": row.getIsSelected(),
                          })}
                          onClick={
                            isNonClickable
                              ? (e) => e.stopPropagation()
                              : undefined
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No results.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </div>
      <DataTablePaginate
        table={table}
        pagination={pagination}
        tableParams={tableParams}
      />
    </div>
  );
};

export default Datatable;
